import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import StyledMatchWatch from './StyledMatchWatch';

import { selectMatch, selectMatchLength, selectMatchStatusId } from 'features/match/matchSlice';

import { selectIsMatchTimeStopped, selectTime, timeSliceActions } from 'features/time/timeSlice';

import formatTime from 'helpers/formatTime';
import findFirstEvent from 'helpers/findFirstEvent';
import timeDelta from 'helpers/timeDelta';

import usePrevious from 'hooks/usePrevious';

import { Time } from 'interfaces/time';

import MatchTimeInput from 'components/MatchTimeInput';
import getPeriodTimeFromMatchLength from './helpers/getPeriodTimeFromMatchLength';
import { MatchEventType } from '../../enums/matchEventType';
import { MatchStatus } from '../../enums/matchStatus';
import hasExtraTime from '../../helpers/hasExtraTime';
import { isQuotaExceededError } from '../../helpers/isQuotaExceeded';
import ls from 'localstorage-slim';
import { LOCAL_STORAGE_TTL } from '../../constants/localStorage';

const MatchWatch: React.FC = () => {
  const dispatch = useDispatch();

  const matchStatusId = useSelector(selectMatchStatusId);
  const match = useSelector(selectMatch);
  const time = useSelector(selectTime);
  const matchLength = useSelector(selectMatchLength);
  const isMatchTimeStopped = useSelector(selectIsMatchTimeStopped);

  const _hasExtraTime = hasExtraTime(match.matchEvents);

  const { minutes: matchMinute, seconds } = time;

  const prevMatchStatusId = usePrevious(matchStatusId);

  const localStorageId = `MatchWatch.${match.id}`;

  const timestampFirstHalf = ls.get(`${localStorageId}.timestampFirstHalf`) as string;
  const localStorageFirstHalfMinutes = ls.get(`${localStorageId}.firstHalfMinutes`) as string;
  const localStorageFirstHalfSeconds = ls.get(`${localStorageId}.firstHalfSeconds`) as string;
  const firstHalfMinutes = localStorageFirstHalfMinutes ? parseInt(localStorageFirstHalfMinutes) : 0;
  const firstHalfSeconds = localStorageFirstHalfSeconds ? parseInt(localStorageFirstHalfSeconds) : 0;

  const timestampSecondHalf = ls.get(`${localStorageId}.timestampSecondHalf`) as string;
  const localStorageSecondHalfMinutes = ls.get(`${localStorageId}.secondHalfMinutes`) as string;
  const localStorageSecondHalfSeconds = ls.get(`${localStorageId}.secondHalfSeconds`) as string;
  const secondHalfMinutes = localStorageSecondHalfMinutes ? parseInt(localStorageSecondHalfMinutes) : 0;
  const secondHalfSeconds = localStorageSecondHalfSeconds ? parseInt(localStorageSecondHalfSeconds) : 0;

  const timestampFirstHalfExtraTime = ls.get(`${localStorageId}.timestampFirstHalfExtraTime`) as string;
  const localStorageFirstHalfExtraTimeMinutes = ls.get(`${localStorageId}.firstHalfExtraTimeMinutes`) as string;
  const localStorageFirstHalfExtraTimeSeconds = ls.get(`${localStorageId}.firstHalfExtraTimeSeconds`) as string;
  const firstHalfExtraTimeMinutes = localStorageFirstHalfExtraTimeMinutes
    ? parseInt(localStorageFirstHalfExtraTimeMinutes)
    : 0;
  const firstHalfExtraTimeSeconds = localStorageFirstHalfExtraTimeSeconds
    ? parseInt(localStorageFirstHalfExtraTimeSeconds)
    : 0;

  const timestampSecondHalfExtraTime = ls.get(`${localStorageId}.timestampSecondHalfExtraTime`) as string;
  const localStorageSecondHalfExtraTimeMinutes = ls.get(`${localStorageId}.secondHalfExtraTimeMinutes`) as string;
  const localStorageSecondHalfExtraTimeSeconds = ls.get(`${localStorageId}.secondHalfExtraTimeSeconds`) as string;
  const secondHalfExtraTimeMinutes = localStorageSecondHalfExtraTimeMinutes
    ? parseInt(localStorageSecondHalfExtraTimeMinutes)
    : 0;
  const secondHalfExtraTimeSeconds = localStorageSecondHalfExtraTimeSeconds
    ? parseInt(localStorageSecondHalfExtraTimeSeconds)
    : 0;
  const firstHalfEvent = findFirstEvent(match.matchEvents, MatchEventType.MATCH_KICKS_OFF);
  const secondHalfEvent = findFirstEvent(match.matchEvents, MatchEventType.SECOND_HALF);
  const firstHalfExtraTimeEvent = findFirstEvent(match.matchEvents, MatchEventType.FIRST_HALF_EXTRA_TIME);
  const secondHalfExtraTimeEvent = findFirstEvent(match.matchEvents, MatchEventType.SECOND_HALF_EXTRA_TIME);

  // initialize
  const initialize = () => {
    const { minutes, seconds } = getTimeMinutes();

    dispatch(timeSliceActions.setMatchTime({ minutes, seconds }));
  };

  const getTimeMinutes = () => {
    let minutes = 0;
    let seconds = 0;

    switch (matchStatusId) {
      case MatchStatus.FIRST_HALF:
        if (firstHalfMinutes && timestampFirstHalf) {
          const timestampDelta = timeDelta(timestampFirstHalf);
          minutes = firstHalfMinutes + timestampDelta.minutes;
          seconds = firstHalfSeconds + timestampDelta.seconds;
        } else if (firstHalfEvent && firstHalfEvent.timestamp) {
          const timestampDelta = timeDelta(firstHalfEvent.timestamp);
          minutes = timestampDelta.minutes;
          seconds = timestampDelta.seconds;
        }
        break;

      case MatchStatus.SECOND_HALF:
        if (secondHalfMinutes && timestampSecondHalf) {
          const timestampDelta = timeDelta(timestampSecondHalf);
          minutes = secondHalfMinutes + timestampDelta.minutes;
          seconds = secondHalfSeconds + timestampDelta.seconds;
        } else if (secondHalfEvent && secondHalfEvent.timestamp) {
          const timestampDelta = timeDelta(secondHalfEvent.timestamp);
          minutes = timestampDelta.minutes + getPeriodTimeFromMatchLength(MatchStatus.SECOND_HALF, matchLength);
          seconds = timestampDelta.seconds;
        }
        break;

      case MatchStatus.FIRST_HALF_EXTRA_TIME:
        if (firstHalfExtraTimeMinutes && timestampFirstHalfExtraTime) {
          const timestampDelta = timeDelta(timestampFirstHalfExtraTime);
          minutes = firstHalfExtraTimeMinutes + timestampDelta.minutes;
          seconds = firstHalfExtraTimeSeconds + timestampDelta.seconds;
        } else if (firstHalfExtraTimeEvent && firstHalfExtraTimeEvent.timestamp) {
          const timestampDelta = timeDelta(firstHalfExtraTimeEvent.timestamp);
          minutes =
            timestampDelta.minutes + getPeriodTimeFromMatchLength(MatchStatus.FIRST_HALF_EXTRA_TIME, matchLength);
          seconds = timestampDelta.seconds;
        }
        break;

      case MatchStatus.SECOND_HALF_EXTRA_TIME:
        if (secondHalfExtraTimeMinutes && timestampSecondHalfExtraTime) {
          const timestampDelta = timeDelta(timestampSecondHalfExtraTime);
          minutes = secondHalfExtraTimeMinutes + timestampDelta.minutes;
          seconds = secondHalfExtraTimeSeconds + timestampDelta.seconds;
        } else if (secondHalfExtraTimeEvent && secondHalfExtraTimeEvent.timestamp) {
          const timestampDelta = timeDelta(secondHalfExtraTimeEvent.timestamp);
          minutes =
            timestampDelta.minutes + getPeriodTimeFromMatchLength(MatchStatus.SECOND_HALF_EXTRA_TIME, matchLength);
          seconds = timestampDelta.seconds;
        }
        break;
    }

    if (seconds >= 60) {
      seconds -= 60;
      minutes++;
    }

    return { minutes, seconds };
  };

  useEffect(initialize, []);

  const iterate = () => {
    const interval = setInterval(() => {
      let isPaused = false;

      if (
        matchStatusId === MatchStatus.HALF_TIME ||
        matchStatusId === MatchStatus.HALF_TIME_EXTRA_TIME ||
        matchStatusId === MatchStatus.PAUSE_BEFORE_EXTRA_TIME ||
        matchStatusId === MatchStatus.NOT_STARTED ||
        matchStatusId === MatchStatus.PLAYED ||
        isMatchTimeStopped
      ) {
        isPaused = true;
      }

      if (!isPaused) {
        const { minutes, seconds } = getTimeMinutes();
        dispatch(timeSliceActions.setMatchTime({ minutes: minutes, seconds: seconds }));
      }
    }, 1000);

    return () => clearInterval(interval);
  };

  useEffect(iterate, [matchStatusId, seconds, isMatchTimeStopped]);

  const handleChange = () => {
    if (prevMatchStatusId) {
      switch (matchStatusId) {
        case MatchStatus.FIRST_HALF:
          dispatch(timeSliceActions.setMatchTime({ minutes: 0, seconds: 0 }));
          break;

        case MatchStatus.SECOND_HALF:
          dispatch(
            timeSliceActions.setMatchTime({
              minutes: getPeriodTimeFromMatchLength(MatchStatus.SECOND_HALF, matchLength),
              seconds: 0,
            }),
          );
          break;

        case MatchStatus.FIRST_HALF_EXTRA_TIME:
          dispatch(
            timeSliceActions.setMatchTime({
              minutes: getPeriodTimeFromMatchLength(MatchStatus.FIRST_HALF_EXTRA_TIME, matchLength),
              seconds: 0,
            }),
          );
          break;

        case MatchStatus.SECOND_HALF_EXTRA_TIME:
          dispatch(
            timeSliceActions.setMatchTime({
              minutes: getPeriodTimeFromMatchLength(MatchStatus.SECOND_HALF_EXTRA_TIME, matchLength),
              seconds: 0,
            }),
          );
          break;
      }
      dispatch(timeSliceActions.emptyLocalStorage(match.id));
    }
  };

  useEffect(handleChange, [matchStatusId]);

  if (
    matchStatusId === MatchStatus.HALF_TIME ||
    matchStatusId === MatchStatus.HALF_TIME_EXTRA_TIME ||
    matchStatusId === MatchStatus.PAUSE_BEFORE_EXTRA_TIME
  ) {
    return (
      <StyledMatchWatch noWidth>
        <FormattedMessage id="states.pause" />
      </StyledMatchWatch>
    );
  } else if (matchStatusId === MatchStatus.PLAYED) {
    return (
      <StyledMatchWatch noWidth>
        <FormattedMessage id="states.ended" />
      </StyledMatchWatch>
    );
  } else if (matchStatusId === MatchStatus.PENALTIES) {
    if (_hasExtraTime) {
      const time = getPeriodTimeFromMatchLength(MatchStatus.PENALTIES, match.matchLength);
      return (
        <StyledMatchWatch noWidth>
          <div className="clock">{time}:00</div>
        </StyledMatchWatch>
      );
    }
    const time = getPeriodTimeFromMatchLength(MatchStatus.FIRST_HALF_EXTRA_TIME, match.matchLength);
    return (
      <StyledMatchWatch noWidth>
        <div className="clock">{time}:00</div>
      </StyledMatchWatch>
    );
  }

  const onMatchTimeInputUpdate = (time: Time) => {
    const now = new Date();
    switch (matchStatusId) {
      case MatchStatus.FIRST_HALF:
        try {
          ls.set(`${localStorageId}.firstHalfMinutes`, time.minutes.toString(), { ttl: LOCAL_STORAGE_TTL });
          ls.set(`${localStorageId}.firstHalfSeconds`, time.seconds.toString(), { ttl: LOCAL_STORAGE_TTL });
          ls.set(`${localStorageId}.timestampFirstHalf`, now.toISOString(), { ttl: LOCAL_STORAGE_TTL });
        } catch (e) {
          console.log('Error in local storage', e);
          if (isQuotaExceededError(e)) {
            ls.flush();
          }
        }
        break;
      case MatchStatus.SECOND_HALF:
        try {
          ls.set(`${localStorageId}.secondHalfMinutes`, time.minutes.toString(), { ttl: LOCAL_STORAGE_TTL });
          ls.set(`${localStorageId}.secondHalfSeconds`, time.seconds.toString(), { ttl: LOCAL_STORAGE_TTL });
          ls.set(`${localStorageId}.timestampSecondHalf`, now.toISOString(), { ttl: LOCAL_STORAGE_TTL });
        } catch (e) {
          console.log('Error in local storage', e);
          if (isQuotaExceededError(e)) {
            ls.flush();
          }
        }
        break;
      case MatchStatus.FIRST_HALF_EXTRA_TIME:
        try {
          ls.set(`${localStorageId}.firstHalfExtraTimeMinutes`, time.minutes.toString(), {
            ttl: LOCAL_STORAGE_TTL,
          });
          ls.set(`${localStorageId}.firstHalfExtraTimeSeconds`, time.seconds.toString(), {
            ttl: LOCAL_STORAGE_TTL,
          });
          ls.set(`${localStorageId}.timestampFirstHalfExtraTime`, now.toISOString(), {
            ttl: LOCAL_STORAGE_TTL,
          });
        } catch (e) {
          console.log('Error in local storage', e);
          if (isQuotaExceededError(e)) {
            ls.flush();
          }
        }
        break;
      case MatchStatus.SECOND_HALF_EXTRA_TIME:
        try {
          ls.set(`${localStorageId}.secondHalfExtraTimeMinutes`, time.minutes.toString(), {
            ttl: LOCAL_STORAGE_TTL,
          });
          ls.set(`${localStorageId}.secondHalfExtraTimeSeconds`, time.seconds.toString(), {
            ttl: LOCAL_STORAGE_TTL,
          });
          ls.set(`${localStorageId}.timestampSecondHalfExtraTime`, now.toISOString(), {
            ttl: LOCAL_STORAGE_TTL,
          });
        } catch (e) {
          console.log('Error in local storage', e);
          if (isQuotaExceededError(e)) {
            ls.flush();
          }
        }
        break;
    }

    dispatch(timeSliceActions.setMatchTime(time));
  };

  let className = '';
  let thresholdMinute;
  switch (matchStatusId) {
    case MatchStatus.FIRST_HALF:
      thresholdMinute = getPeriodTimeFromMatchLength(MatchStatus.SECOND_HALF);
      break;
    case MatchStatus.SECOND_HALF:
      thresholdMinute = getPeriodTimeFromMatchLength(MatchStatus.FIRST_HALF_EXTRA_TIME);
      break;
    case MatchStatus.FIRST_HALF_EXTRA_TIME:
      thresholdMinute = getPeriodTimeFromMatchLength(MatchStatus.SECOND_HALF_EXTRA_TIME);
      break;
    case MatchStatus.SECOND_HALF_EXTRA_TIME:
      thresholdMinute = getPeriodTimeFromMatchLength(MatchStatus.PENALTIES);
      break;
  }

  if (matchMinute >= thresholdMinute) {
    className = 'overtime';
  }

  return (
    <StyledMatchWatch>
      <div className={'clock ' + className}>{formatTime(matchMinute, seconds)}</div>
      <MatchTimeInput
        matchStatusId={matchStatusId}
        time={{ minutes: matchMinute, seconds }}
        onUpdate={onMatchTimeInputUpdate}
      />
    </StyledMatchWatch>
  );
};

export default MatchWatch;
